import * as React from 'react';
import Button from '@mui/material/Button';
import { Alert, Autocomplete, Avatar, Box, Chip, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Pagination, Select, Stack, TextField, Typography } from '@mui/material';
import { getUserTags, getUsersPaginated } from '../Api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridCellParams, GridValueGetterParams } from '@mui/x-data-grid/models/params/gridCellParams';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import SubscriptionTierChip from '../components/SubscriptionTierChip';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import { UserTagCombinationMode } from '../models/UserTagCombinationMode';

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

const USER_TAG_COMBINATION_MODE_OPTIONS: UserTagCombinationMode[] = ['ALL', 'ANY'];

const getUserTagCombinationModeFromQuery = (value: string | null): UserTagCombinationMode => {
    return USER_TAG_COMBINATION_MODE_OPTIONS.includes(value as UserTagCombinationMode)
        ? value as UserTagCombinationMode
        : 'ALL';
}

const areStringArraysEqual = (left: string[], right: string[]) => {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((value, index) => value === right[index]);
}

export default function UsersPage() {
    const defaultLimit = 25;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loaded, setLoaded] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [limit] = React.useState(defaultLimit);
    const [totalPages, setTotalPages] = React.useState(1);
    const [searchTerm, setSearchTerm] = React.useState(searchParams.get('searchTerm') ?? '');
    const [selectedUserTags, setSelectedUserTags] = React.useState<string[]>(searchParams.getAll('userTags'));
    const [tagCombinationMode, setTagCombinationMode] = React.useState<UserTagCombinationMode>(
        getUserTagCombinationModeFromQuery(searchParams.get('tagCombinationMode'))
    );
    const [availableUserTags, setAvailableUserTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [users, setUsers] = React.useState<AccountMainInfoDto[]>([]);
    const [error, setError] = React.useState('');
    const [retry, setRetry] = React.useState(0);

    const onUserClicked = async (userId: number) => {
        window.localStorage.setItem('webGames.playAsUserId', String(userId));
        navigate('/users/' + userId);
    };

    const onMessageClicked = async (userId: number) => {
        navigate('/users/' + userId + '?tab=chat');
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'id', width: 128 },
        {
            field: 'photoUrl', headerName: 'avatar ', width: 130, renderCell: (params: GridCellParams) =>
                <React.Fragment><Avatar
                    src={params.row.photoUrl}
                    sx={{ width: 40, height: 40 }}
                /></React.Fragment>
        },
        { field: 'email', headerName: 'email', width: 250 },
        { field: 'name', headerName: 'name', width: 150 },
        {
            field: 'subscriptionTier', headerName: 'Tier', width: 90, sortable: false, renderCell: (params: GridCellParams) =>
                <SubscriptionTierChip tier={params.row.subscriptionTier} />
        },
        {
            field: 'tags', headerName: 'Tags', width: 280, sortable: false, renderCell: (params: GridCellParams) =>
                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ py: 0.5 }}>
                    {(params.row.tags as string[]).map(tag => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                </Stack>
        },
        {
            field: 'osType', headerName: 'OS', width: 128, renderCell: (params: GridCellParams) =>
                <React.Fragment><img src={params.row.osType == 'android' ? '/android_logo.png' : '/ios_logo.png'} width={40} height={40} /></React.Fragment>
        },
        {
            field: 'createdAt', headerName: 'CreatedAt', width: 200, valueGetter: (params: GridValueGetterParams) =>
                new Date(params.row.createdAt).toLocaleString()
        },
        {
            field: 'actions', headerName: 'Actions', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment><Button variant="contained" fullWidth={true} style={{ height: '32px' }} onClick={() => onUserClicked(params.row.id)}>Details</Button>&nbsp;
                    <Button variant="outlined" fullWidth={true} style={{ height: '32px' }} onClick={() => onMessageClicked(params.row.id)}>Chat</Button></React.Fragment>
        },
    ];

    React.useEffect(() => {
        let cancelled = false;

        (async () => {
            const response = await getUserTags();
            if (cancelled) return;

            setAvailableUserTags((response ?? [])
                .slice()
                .sort((left, right) => left.tagName.localeCompare(right.tagName)));
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    React.useEffect(() => {
        const params = new URLSearchParams();
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm.length > 0) {
            params.set('searchTerm', trimmedSearchTerm);
        }
        selectedUserTags.forEach(tag => params.append('userTags', tag));
        if (selectedUserTags.length > 0) {
            params.set('tagCombinationMode', tagCombinationMode);
        }
        if (params.toString() !== searchParams.toString()) {
            setSearchParams(params, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, selectedUserTags, tagCombinationMode, setSearchParams]);

    React.useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setError('');
        const timeoutId = window.setTimeout(async () => {
            try {
                const response = await getUsersPaginated(
                    (currentPage - 1) * limit,
                    limit,
                    searchTerm.trim().length > 0 ? searchTerm.trim() : undefined,
                    selectedUserTags,
                    tagCombinationMode,
                );
                if (cancelled) return;
                if (!response) throw new Error('User search is unavailable.');
                const items = response.items;
                const totalItems = response.totalItems;

                setUsers(items);
                setTotalPages(Math.max(1, Math.ceil(totalItems / limit)));
            } catch {
                if (!cancelled) {
                    setUsers([]);
                    setTotalPages(1);
                    setError('Users could not be loaded. Search may be unavailable; please retry.');
                }
            } finally { if (!cancelled) setLoaded(true); }
        }, 400);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [currentPage, limit, searchTerm, selectedUserTags, tagCombinationMode, retry]);

    return (
        <div><Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} alignItems='center'>
                <Grid item xs={0} sm={1} md={3} lg={5} />
                <Grid item xs={1} sm={1} md={1} lg={1}>
                    <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/delete-account-requests')}>Account deletion requests</Button>
                </Grid>
            </Grid>
            <Typography variant="h3">Users</Typography>
            <TextField
                type='search'
                key="searchField"
                label="Search by name, email, or user ID"
                variant="filled"
                fullWidth={true}
                value={searchTerm}
                onChange={e => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                }}
            />
            <Grid container spacing={2} alignItems='stretch'>
                <Grid item xs={12} md={8}>
                    <Autocomplete
                        multiple
                        options={availableUserTags}
                        value={selectedUserTags.map(tagName => (
                            availableUserTags.find(tag => tag.tagName === tagName) ?? {
                                id: tagName,
                                tagName,
                                requirements: [],
                                approximateUsersCount: null,
                                tagRecalculationInfo: null,
                                createdAt: '',
                            }
                        ))}
                        isOptionEqualToValue={(option, value) => option.tagName === value.tagName}
                        getOptionLabel={(option) => option.tagName}
                        onChange={(_, value) => {
                            setSelectedUserTags(value.map(tag => tag.tagName));
                            setCurrentPage(1);
                        }}
                        fullWidth
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Stack spacing={0.25}>
                                    <Typography variant="body2">{option.tagName}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.approximateUsersCount == null
                                            ? 'Approx. users: counting...'
                                            : `Approx. users: ${option.approximateUsersCount.toLocaleString()}`}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.tagName}
                                    label={option.tagName}
                                    size="small"
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Filter by user tags" placeholder="Select one or more tags" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="user-tag-combination-mode-label">Tag combination</InputLabel>
                        <Select
                            labelId="user-tag-combination-mode-label"
                            value={tagCombinationMode}
                            label="Tag combination"
                            onChange={(event) => {
                                setTagCombinationMode(event.target.value as UserTagCombinationMode);
                                setCurrentPage(1);
                            }}
                        >
                            <MenuItem value="ALL">ALL</MenuItem>
                            <MenuItem value="ANY">ANY</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Pagination count={totalPages} size="medium" onChange={(_, p) => setCurrentPage(p)} page={currentPage} />
            {error && <Alert severity="error" sx={{width:'100%'}} action={<Button color="inherit" onClick={()=>setRetry(value=>value+1)}>Retry</Button>}>{error}</Alert>}
            {!loaded ? <Grid item xs={2}><CircularProgress size='100px' style={{ marginBottom: '32px' }} /></Grid> : !error && <DataGrid sx={{ width: '100%' }}
                rows={users}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: { page: 0, pageSize: 25 },
                    },
                }}
                pageSizeOptions={[5, 10]}
                hideFooterPagination
                getRowHeight={() => 'auto'}
            />}
        </Stack >
        </div>
    );
}
